<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" id="addReimbursementModal" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Add Reimbursement</h3>
        </template>

        <slot>
            <form-errors :errors="formErrors"/>

            <div v-if="dataLoaded">
                <!-- Step 1: Companies -->
                <div v-if="step === 'companies'">
                    <p class="mb-0">Select the company or companies this reimbursement will be available for.</p>
                    <company-selector :client="client" :companies="client.companies" v-model="formData.companies"/>
                </div>

                <!-- Step 2: Reimbursement Type -->
                <div v-if="step === 'reimbursement-types'">
                    <p>What reimbursement will be available?</p>
                    <reimbursement-selector v-model="formData.reimbursement_types" :selectableReimbursements="selectableReimbursementTypes" @update:model-value="onUpdateSelectedReimbursementTypes($event)" :hideTabs="true"/>
                </div>

                <!-- Step 3: Customize (skipped if no customizable fields) -->
                <div v-if="step === 'customize'">
                    <p>Provide a name for this reimbursement.</p>
                    <form class="mui-form" @submit.prevent>
                        <template v-for="rt in customizableSelectedTypes" :key="rt.id">
                            <div class="form-row">
                                <div class="col">
                                    <form-input
                                        v-model="formData.names_by_reimbursement_type[rt.id]"
                                        :errors="errors[`name_${rt.id}`]"
                                        type="text"
                                        :label="`Name (base: ${rt.name})`"
                                    />
                                </div>
                            </div>
                        </template>
                    </form>
                </div>

                <!-- Step 4: Positions -->
                <div v-if="step === 'positions'">
                    <p class="mb-2">Select the positions that can use this reimbursement.</p>
                    <position-selector
                        v-if="allCompanyPositions.length"
                        :all-positions="allCompanyPositions"
                        :companies="client.companies"
                        v-model="currentPositions"
                    />
                    <p v-else class="text-muted">No active positions found for the selected companies.</p>
                </div>

                <!-- Step 5: Default Positions -->
                <div v-if="step === 'default-positions'">
                    <p class="mb-2">Select the positions that will use this reimbursement by default.</p>
                    <position-selector
                        v-if="selectedAvailablePositions.length"
                        :all-positions="selectedAvailablePositions"
                        :companies="client.companies"
                        v-model="currentDefaultPositions"
                        :allPositionsLabel="'Available Positions'"
                    />
                    <p v-else class="text-muted">No positions selected as available.</p>
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button v-if="step === 'companies'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-else type="button" class="btn btn-outline-primary" @click.prevent="prevStep">
                    Back
                </button>

                <button v-if="step === 'default-positions'" type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save &amp; Exit
                </button>
                <button v-else type="button" class="btn btn-primary" @click.prevent="nextStep">
                    Continue
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import PositionSelector from '@/pages/client/Settings/components/PositionSelector'
import CompanySelector from '@/pages/client/Settings/components/CompanySelector'
import ReimbursementSelector from '@/pages/client/Settings/components/ReimbursementSelector'

export default {
    props: ['client', 'clientReimbursementTypes'],
    mixins: [FormMixin, ModalMixin],
    components: {PositionSelector, CompanySelector, ReimbursementSelector},
    data() {
        return {
            steps: ['companies', 'reimbursement-types', 'customize', 'positions', 'default-positions'],
            step: 'companies',
            formData: this.makeFormData(),
            globalReimbursementTypes: [],
            allPositions: [],
            positionsLoaded: false,
            globalReimbursementTypesLoaded: false,
        }
    },
    mounted() {
        this.getPositions()
        this.getGlobalReimbursementTypes()
    },
    watch: {
        dataLoaded(val) {
            if (val) {
                this.$store.dispatch('STOP_LOADING')
            }
        },
    },
    computed: {
        dataLoaded() {
            return this.positionsLoaded && this.globalReimbursementTypesLoaded
        },
        allCompanyPositions() {
            return this.allPositions.filter(p => this.formData.companies.includes(p.company_id))
        },
        selectableReimbursementTypes() {
            return this.globalReimbursementTypes.filter(rt => {
                if (rt.allow_multiple_per_company) return true
                const existingForSelectedCompanies = this.clientReimbursementTypes.filter(
                    crt => this.formData.companies.includes(crt.company_id) && crt.reimbursement_type_id == rt.id
                )
                return existingForSelectedCompanies.length === 0
            })
        },
        selectedReimbursementTypes() {
            return this.globalReimbursementTypes.filter(rt => this.formData.reimbursement_types.includes(rt.id))
        },
        customizableSelectedTypes() {
            return this.selectedReimbursementTypes.filter(
                rt => rt.fields_to_customize && rt.fields_to_customize.includes('name')
            )
        },
        hasCustomizableFields() {
            return this.customizableSelectedTypes.length > 0
        },
        // For the positions and default-positions steps we use the first (and typically only) selected type
        currentTypeId() {
            return this.formData.reimbursement_types[0] || null
        },
        currentPositions: {
            get() {
                return this.formData.positions_by_reimbursement_type[this.currentTypeId] || []
            },
            set(val) {
                if (this.currentTypeId) {
                    this.formData.positions_by_reimbursement_type[this.currentTypeId] = val
                }
            },
        },
        currentDefaultPositions: {
            get() {
                return this.formData.default_positions_by_reimbursement_type[this.currentTypeId] || []
            },
            set(val) {
                if (this.currentTypeId) {
                    this.formData.default_positions_by_reimbursement_type[this.currentTypeId] = val
                }
            },
        },
        selectedAvailablePositions() {
            const selectedIds = this.currentPositions
            return this.allCompanyPositions.filter(p => selectedIds.includes(p.id))
        },
    },
    methods: {
        getPositions() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/positions`).then(resp => {
                this.allPositions = resp
                this.allPositions.sort((a, b) => a.terminal_value.toLowerCase() > b.terminal_value.toLowerCase() ? 1 : -1)
                this.positionsLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        getGlobalReimbursementTypes() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/admin/tax-engine/reimbursement-types`).then(resp => {
                this.globalReimbursementTypes = resp
                this.globalReimbursementTypesLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        makeFormData() {
            return {
                companies: [],
                reimbursement_types: [],
                names_by_reimbursement_type: {},
                positions_by_reimbursement_type: {},
                default_positions_by_reimbursement_type: {},
            }
        },
        resetErrors() {
            this.errors = {}
            this.formErrors = []
        },
        nextStep() {
            let idx = this.steps.indexOf(this.step) + 1
            if (this.steps[idx] === 'customize' && !this.hasCustomizableFields) {
                idx++
            }
            this.step = this.steps[idx]
        },
        prevStep() {
            let idx = this.steps.indexOf(this.step) - 1
            if (this.steps[idx] === 'customize' && !this.hasCustomizableFields) {
                idx--
            }
            this.step = this.steps[idx]
        },
        onUpdateSelectedReimbursementTypes(reimbursementTypeIds) {
            reimbursementTypeIds.forEach(reimbursementTypeId => {
                if (reimbursementTypeId) {
                    if (!this.formData.positions_by_reimbursement_type[reimbursementTypeId]) {
                        this.formData.positions_by_reimbursement_type[reimbursementTypeId] = []
                    }
                    if (!this.formData.default_positions_by_reimbursement_type[reimbursementTypeId]) {
                        this.formData.default_positions_by_reimbursement_type[reimbursementTypeId] = []
                    }
                    if (!this.formData.names_by_reimbursement_type[reimbursementTypeId]) {
                        this.formData.names_by_reimbursement_type[reimbursementTypeId] = ''
                    }
                }
            })
        },
        getFormData() {
            return {
                companies: this.formData.companies,
                reimbursement_types: this.formData.reimbursement_types,
                names_by_reimbursement_type: this.formData.names_by_reimbursement_type,
                positions_by_reimbursement_type: this.formData.positions_by_reimbursement_type,
                default_positions_by_reimbursement_type: this.formData.default_positions_by_reimbursement_type,
            }
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            this.resetErrors()
            return this.$api.post(`/clients/${this.client.id}/settings/reimbursement-types`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        },
    }
}
</script>
